import { Field, FieldError, FieldLabel } from '@playbooks/ui/components/field'
import { Question } from '@playbooks/core/src/interfaces/ActionCard'

import MarkdownText from '../../../components/MarkdownText'
import Highlighter from '../components/Highlighter'
import { JSX, useState } from 'react'
import { InputProps } from '../types'
import { useAppContext } from '../../../contexts/AppContext'
import useMountEffect from '../../../hooks/useMountEffect'

export default function FieldWrapper({
  question,
  render,
  onChange,
}: {
  question: Question
  render: (onChange: InputProps['onChange'], invalid?: boolean) => JSX.Element
  onChange: InputProps['onChange']
  isDocView?: boolean
}) {
  const {
    settings: { showMandatoryFields },
    highlightElementsServiceRef,
  } = useAppContext()
  const [errorMessageState, setErrorMessageState] = useState<string | null>(null)

  const handleChange = (value: unknown, errorMessage?: string) => {
    setErrorMessageState(errorMessage || null)

    if (errorMessage) {
      highlightElementsServiceRef.current.addIds([question.id])
    } else {
      highlightElementsServiceRef.current.deleteIds([question.id])
    }

    if (!errorMessage) {
      onChange(value)
    }
  }

  useMountEffect(() => () => {
    // cleanup highlights on unmount, so if the question gets unmounted
    highlightElementsServiceRef.current.deleteIds([question.id])
  })

  const questionText = showMandatoryFields ? `${question.text}${question.required ? ' *' : ''}` : question.text

  return (
    <Field
      data-field-wrapper
      data-field-wrapper-error-message={errorMessageState}
      data-invalid={!!errorMessageState}
      className="AC_question relative gap-2"
      orientation="responsive"
    >
      <FieldLabel className="text-sm" htmlFor={`question-${question.id}`}>
        <MarkdownText text={questionText} />
      </FieldLabel>

      <Highlighter id={question.id} showFrame={!!question.required}>
        {/* This highlightElementsServiceRef is defined in the AppContextProvider and never changes so we can safely use it like this */}
        {/* Also, the ref isn't actually used on render, only in callbacks */}
        {/* eslint-disable-next-line react-hooks/refs */}
        <div className="w-full">{render(handleChange, !!errorMessageState)}</div>
      </Highlighter>

      {errorMessageState && <FieldError className="absolute -bottom-4 text-xs">{errorMessageState}</FieldError>}
    </Field>
  )
}
